import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Box,
  Typography,
  Avatar,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Tooltip,
  Chip,
} from "@mui/material";
import { useState } from "react";
import { TrainingSession } from "../api/types";
import { trainingSetService } from "../api/service.ts";

type TrainingDetailDialogProps = {
  training: TrainingSession;
  onClose: () => void;
};

export default function TrainingDetailDialog({ training, onClose }: TrainingDetailDialogProps) {
  const [tabIndex, setTabIndex] = useState(0);
  const [sets, setSets] = useState(training.sets);

  if (!training) return null;

  const handleChange = (_: React.SyntheticEvent, newValue: number) => setTabIndex(newValue);

  const handleRemovePlayer = async (setId: number, team: "teamA" | "teamB", playerId: number) => {
    await trainingSetService.removePlayer(setId, team, playerId);
    setSets(prev =>
      prev.map(s =>
        s.id === setId
          ? { ...s, [team]: s[team].filter(p => p.id !== playerId) }
          : s
      )
    );
  };

  const handleMovePlayer = async (
    setId: number,
    fromTeam: "teamA" | "teamB",
    toTeam: "teamA" | "teamB",
    playerId: number
  ) => {
    await trainingSetService.movePlayer(setId, fromTeam, toTeam, playerId);
    setSets(prev =>
      prev.map(s => {
        if (s.id !== setId) return s;
        const player = s[fromTeam].find(p => p.id === playerId);
        if (!player) return s;
        return {
          ...s,
          [fromTeam]: s[fromTeam].filter(p => p.id !== playerId),
          [toTeam]: [...s[toTeam], player],
        };
      })
    );
  };

  const handleUpdateScore = async ( newScoreA: number, newScoreB: number,setId?: number) => {
    if(!setId) return;
    await trainingSetService.updateScore(setId, newScoreA, newScoreB);
    setSets(prev => prev.map(s => (s.id === setId ? { ...s, scoreA: newScoreA, scoreB: newScoreB } : s)));
  };

  const renderTeamTable = (
    currentSet: any,
    teamName: "teamA" | "teamB",
    label: string,
    color: string,
    players: any[]
  ) => {
    const sortedPlayers = [...players].sort((a, b) =>
      a.nickname.localeCompare(b.nickname, "it", { sensitivity: "base" })
    );

    return (
      <TableContainer component={Paper} sx={{ flex: 1, backgroundColor: `${color}10` }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: `${color}30` }}>
              <TableCell colSpan={3}>
                <Typography fontWeight="bold" textAlign="center">
                  {label}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Giocatore</TableCell>
              <TableCell>Skill</TableCell>
              <TableCell align="center">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedPlayers.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar
                      sx={{
                        bgcolor: p.gender === "F" ? "#f48fb1" : color,
                        width: 28,
                        height: 28,
                        fontSize: 14,
                      }}
                    >
                      {p.nickname.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography>{p.nickname}</Typography>
                  </Stack>
                </TableCell>
                <TableCell>{p.skill ?? "-"}</TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={1} justifyContent="center">
                    <Tooltip title="Rimuovi dal set">
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleRemovePlayer(currentSet.id, teamName, p.id)}
                      >
                        ❌
                      </Button>
                    </Tooltip>
                    <Tooltip title="Sposta all’altro team">
                      <Button
                        size="small"
                        color="secondary"
                        onClick={() =>
                          handleMovePlayer(
                            currentSet.id,
                            teamName,
                            teamName === "teamA" ? "teamB" : "teamA",
                            p.id
                          )
                        }
                      >
                        🔁
                      </Button>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Dialog open={!!training} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ backgroundColor: "primary.main" }}>
        🏐 Dettagli allenamento del {training.date}
      </DialogTitle>

      <DialogContent sx={{ pt: 1, overflowY: "visible" }}>
        <Tabs
          value={tabIndex}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
        >
          {sets.map((_, i) => (
            <Tab key={i} label={`Set ${i + 1}`} />
          ))}
        </Tabs>

        {sets.map((set, i) => (
          <Box key={i} hidden={tabIndex !== i}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              {renderTeamTable(set, "teamA", "🔵 Team A", "#1976d2", set.teamA)}
              {renderTeamTable(set, "teamB", "🔴 Team B", "#d32f2f", set.teamB)}
            </Stack>

            {set.bench && set.bench.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>🪑 Panchina</Typography>
                <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
                  {set.bench.map((p) => (
                    <Chip key={p.id} label={p.nickname} size="small" variant="outlined" />
                  ))}
                </Stack>
              </Box>
            )}

            <Box mt={2} textAlign="center">
              <Typography variant="h6" gutterBottom>
                Risultato:
              </Typography>
              <Stack direction="row" justifyContent="center" spacing={1} mt={1}>
                <input
                  type="number"
                  value={set.scoreA}
                  onChange={(e) =>
                    handleUpdateScore(Number(e.target.value), set.scoreB,set.id,)
                  }
                  style={{ width: 50, textAlign: "center" }}
                />
                <Typography variant="h6"> - </Typography>
                <input
                  type="number"
                  value={set.scoreB}
                  onChange={(e) =>
                    handleUpdateScore( set.scoreA, Number(e.target.value),set.id)
                  }
                  style={{ width: 50, textAlign: "center" }}
                />
              </Stack>
            </Box>
          </Box>
        ))}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Chiudi
        </Button>
      </DialogActions>
    </Dialog>
  );
}
